import { type Data, FieldClassification } from "@eai-toolkit/client";
import { Edit } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { useGetClusterConfigQuery } from "@store/api/clusterApi";
import { useUpdateDataObjectMutation } from "@store/api/dataApi";
import { clearCache, updateDataObjectInList } from "@store/slices/dataSlice";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

import {
  CLASSIFICATION_METADATA,
  type NonEmptyFieldClassification,
} from "../../../data/fieldRenderers/Classification";
import type { RowAction } from "../../../data/types";

// Define classification hierarchy (lower index = lower security level)
const CLASSIFICATION_HIERARCHY: Array<FieldClassification | ""> = [
  "",
  FieldClassification.servicenow_public,
  FieldClassification.servicenow_internal_confidential,
  FieldClassification.servicenow_internal_restricted,
  FieldClassification.research_only,
  FieldClassification.third_party_licensed,
  FieldClassification.customer_restricted,
];

const CLASSIFICATION_OPTIONS: Array<{
  value: FieldClassification | "";
  label: string;
  tooltip: string;
}> = [
  ...Object.entries(CLASSIFICATION_METADATA).map(([value, meta]) => ({
    value: value as FieldClassification,
    ...meta,
  })),
];

const getClassificationLevel = (
  classification: FieldClassification | "",
): number => {
  return CLASSIFICATION_HIERARCHY.indexOf(classification);
};

// Filter options based on minimum level
const getFilteredOptions = (
  minClassification: FieldClassification | "",
): typeof CLASSIFICATION_OPTIONS => {
  const minLevel = getClassificationLevel(minClassification);
  return CLASSIFICATION_OPTIONS.filter((option) => {
    const optionLevel = getClassificationLevel(option.value);
    return optionLevel >= minLevel;
  });
};

interface EditClassificationDialogProps {
  open: boolean;
  onClose: () => void;
  dataObject: Data;
}

function EditClassificationDialog({
  open,
  onClose,
  dataObject,
}: EditClassificationDialogProps) {
  const [value, setValue] = useState<FieldClassification | "">(
    (dataObject.classification as FieldClassification) || "",
  );
  const [updateDataObject, { isLoading }] = useUpdateDataObjectMutation();
  const { data: clusterConfig } = useGetClusterConfigQuery();
  const dispatch = useDispatch();
  const [error, setError] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValue, setPendingValue] = useState<FieldClassification | "">(
    "",
  );

  // Check if classification validation is enabled
  const isValidationEnabled =
    clusterConfig?.validateDataClassification === true;

  // Get filtered options based on current classification
  const currentClassification =
    (dataObject.classification as FieldClassification) || "";
  const filteredOptions = isValidationEnabled
    ? getFilteredOptions(currentClassification)
    : [
        { value: "" as const, label: "-", tooltip: "No classification" },
        ...CLASSIFICATION_OPTIONS,
      ];

  const handleSave = async () => {
    const previousValue =
      (dataObject.classification as FieldClassification) || "";

    // Validate that data has a fullName
    if (!dataObject.fullName) {
      setError("Cannot update classification for data without a name");
      return;
    }

    // Clear any previous errors
    setError("");

    try {
      const updatePayload = {
        id: dataObject.id,
        classification: value as FieldClassification,
      };

      // Optimistically update the local state in Redux store
      dispatch(
        updateDataObjectInList({
          id: dataObject.id,
          updates: { classification: value as FieldClassification },
        }),
      );

      await updateDataObject(updatePayload).unwrap();

      // Clear cache after successful update to ensure sorting fetches fresh data
      dispatch(clearCache());

      onClose();
    } catch (error: any) {
      console.error("Error updating classification:", error);
      // Revert both local state and Redux store on error
      setValue(previousValue);
      dispatch(
        updateDataObjectInList({
          id: dataObject.id,
          updates: { classification: previousValue },
        }),
      );

      // Extract error message from the API response
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        error?.data?.error ||
        "Failed to update classification. Please try again.";
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    setValue((dataObject.classification as FieldClassification) || "");
    setError("");
    setShowConfirmation(false);
    setPendingValue("");
    onClose();
  };

  const handleClassificationChange = (newValue: FieldClassification | "") => {
    if (
      isValidationEnabled &&
      newValue &&
      newValue !== currentClassification &&
      newValue !== FieldClassification.servicenow_public
    ) {
      setPendingValue(newValue);
      setShowConfirmation(true);
    } else {
      setValue(newValue);
    }
  };

  const handleConfirmationOk = () => {
    setValue(pendingValue);
    setShowConfirmation(false);
    setPendingValue("");
  };

  const handleConfirmationCancel = () => {
    setShowConfirmation(false);
    setPendingValue("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      PaperProps={{ sx: { minWidth: "min(444px, calc(100% - 64px))" } }}
    >
      <DialogContent sx={{ pt: 3 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, overflowWrap: "anywhere" }}
        >
          Data: {dataObject.fullName || dataObject.id}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Select
          value={value}
          onChange={(e) =>
            handleClassificationChange(
              e.target.value as FieldClassification | "",
            )
          }
          disabled={isLoading}
          size="small"
          fullWidth
          displayEmpty
        >
          {filteredOptions.map((option) => (
            <MenuItem key={option.value || "empty"} value={option.value}>
              <Tooltip title={option.tooltip} placement="right" arrow>
                <span style={{ width: "100%" }}>{option.label}</span>
              </Tooltip>
            </MenuItem>
          ))}
        </Select>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {value &&
            CLASSIFICATION_METADATA[value as NonEmptyFieldClassification]
              ?.tooltip}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          Save
        </Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={handleConfirmationCancel}
        maxWidth="md"
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Alert
            severity="warning"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: { sm: "nowrap" } }}>
              Are you sure you want to change the classification to{" "}
              <strong>
                {pendingValue &&
                  CLASSIFICATION_METADATA[
                    pendingValue as NonEmptyFieldClassification
                  ]?.label}
              </strong>
              ?
            </Typography>
            <Typography variant="body2" sx={{ width: 0, minWidth: "100%" }}>
              You will NOT be able to downgrade this classification later.
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 2, width: 0, minWidth: "100%" }}
            >
              Classification priority order is:{" "}
              <strong>
                {"'servicenow public' < 'servicenow internal confidential' < " +
                  "'servicenow internal restricted' < 'research only' < " +
                  "'third party licensed' < 'customer restricted'"}
              </strong>
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmationCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmationOk}
            variant="contained"
            disabled={isLoading}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export function useEditClassificationAction(): {
  action: RowAction<Data>;
  dialog: React.ReactNode;
} {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<Data | null>(null);

  const action: RowAction<Data> = {
    label: "Edit classification",
    getAction: (row: Data) => ({
      onClick: () => {
        setSelectedData(row);
        setDialogOpen(true);
      },
    }),
    icon: (disabled: boolean) => (
      <Edit fontSize="small" color={disabled ? "disabled" : "action"} />
    ),
  };

  const dialog = selectedData ? (
    <EditClassificationDialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
        setSelectedData(null);
      }}
      dataObject={selectedData}
    />
  ) : null;

  return { action, dialog };
}
